import React, { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";

interface PopularItem {
  name: string;
  count: number;
  revenue: number;
}

interface ProductSalesData {
  date: string;
  sales: number;
  revenue: number;
}

interface Props {
  popularItems: PopularItem[];
  selectedProduct: string | null;
  productSalesData: ProductSalesData[];
  onProductSelect: (name: string) => void;
  height?: number;
}

const ProductPerformanceChart: React.FC<Props> = ({ popularItems, selectedProduct, productSalesData, onProductSelect, height = 320 }) => {
  const selected = useMemo(() => popularItems.find((i) => i.name === selectedProduct), [selectedProduct, popularItems]);
  const FormatAmount = useFormatAmount()

  // ✅ Custom tooltip for better readability
  const CustomTooltip = ({ active, payload, label }: any) =>
    active && payload?.length ? (
      <div className="bg-white p-3 rounded-xl shadow-lg border border-gray-200">
        <p className="text-sm font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((e: any, i: number) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ background: e.color }} />
            <span className="text-gray-600">{e.name}:</span>
            <span className="font-semibold text-gray-900">${e.value?.toFixed(2)}</span>
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div className="w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-3">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Product Performance</h2>
          <p className="text-sm text-gray-500">View sales and revenue trends by product</p>
        </div>

        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 min-w-[180px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          value={selectedProduct || ""}
          onChange={(e) => onProductSelect(e.target.value)}
        >
          <option value="" disabled>
            Select Product
          </option>
          {popularItems.map(({ name }) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Stats */}
      {selected && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="p-3 rounded-lg border border-blue-100 bg-blue-50">
            <p className="text-blue-700 text-sm font-medium">Selected Product</p>
            <p className="text-blue-900 font-bold truncate">{selected.name}</p>
          </div>
          <div className="p-3 rounded-lg border border-green-100 bg-green-50">
            <p className="text-green-700 text-sm font-medium">Total Sales</p>
            <p className="text-green-900 font-bold">{selected.count}</p>
          </div>
          <div className="p-3 rounded-lg border border-purple-100 bg-purple-50">
            <p className="text-purple-700 text-sm font-medium">Total Revenue</p>
            <p className="text-purple-900 font-bold">{FormatAmount(selected.revenue)}</p>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 p-4" style={{ height }}>
        {selectedProduct ? (
          <ResponsiveContainer>
            <LineChart data={productSalesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 12 }} tickMargin={10} />
              <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="sales" stroke="#f59e0b" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 6 }} name="Sales" />
              <Line type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" name="Revenue" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8 text-gray-500">
            <p className="text-lg font-semibold mb-2">No Product Selected</p>
            <p className="text-sm">Choose a product to view performance trends</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPerformanceChart;
